import { viewerMatches, type NavViewer } from '@/components/layout/navigation';
import { paths } from '@/routes/paths';
import { accessModule } from './access';
import { clinicModule } from './clinic';
import { directoryModule } from './directory';
import { fleetModule } from './fleet';
import { logisticsModule } from './logistics';
import { myOrganizationModule } from './myOrganization';
import { pharmacyModule } from './pharmacy';
import { financingModule, marketplaceModule } from './planned';
import { referenceModule } from './reference';
import {
  isBuilt,
  isGridModule,
  type ModuleKey,
  type ModuleManifest,
  type RegistryEntry,
} from './manifest';

/**
 * Every module the console has, in display order — the only file that knows
 * all of them.
 *
 * This one array feeds the apps grid, the module switcher, each module's
 * sidebar and the route table's `RequireModule` wrappers, so a module cannot
 * be drawn and unguarded, or guarded and undrawn. Adding a module is a new
 * manifest file and a line here; no central navigation or route file grows.
 *
 * Gating is the navigation's `viewerMatches` with the same
 * `{ isPlatform, permissions }` viewer, and nothing here ever branches on a
 * role code: a role is only its permission set and its `is_platform` flag.
 */
export const MODULE_REGISTRY: RegistryEntry[] = [
  clinicModule,
  pharmacyModule,
  logisticsModule,
  fleetModule,
  directoryModule,
  referenceModule,
  accessModule,
  myOrganizationModule,
  marketplaceModule,
  financingModule,
];

export function moduleByKey(key: ModuleKey): RegistryEntry | undefined {
  return MODULE_REGISTRY.find((entry) => entry.key === key);
}

/** Every built module, in display order — what the route table is generated from. */
export function builtModules(entries: RegistryEntry[] = MODULE_REGISTRY): ModuleManifest[] {
  return entries.filter(isBuilt);
}

export interface VisibleModule {
  key: ModuleKey;
  titleKey: string;
  descriptionKey: string;
  icon: RegistryEntry['icon'];
  comingSoon?: boolean;
  /** Resolved for this viewer; `null` for a module that is not built yet. */
  to: string | null;
}

/**
 * The tiles on the apps grid. Pure: the same registry and viewer always give
 * the same cards. A card's destination is resolved for the viewer, because the
 * module gate is `anyOf` and a tile must never land anyone on `/forbidden`.
 */
export function visibleModules(
  entries: RegistryEntry[],
  viewer: NavViewer,
  { includeNonGrid = false }: { includeNonGrid?: boolean } = {},
): VisibleModule[] {
  return entries
    .filter((entry) => includeNonGrid || isGridModule(entry))
    .filter((entry) => !isBuilt(entry) || viewerMatches(entry, viewer))
    .map((entry) => ({
      key: entry.key,
      titleKey: entry.titleKey,
      descriptionKey: entry.descriptionKey,
      icon: entry.icon,
      comingSoon: entry.comingSoon === true,
      to: isBuilt(entry) ? entry.landing(viewer) : null,
    }));
}

/** The modules the viewer can actually open — what "has exactly one module" means. */
export function openableModules(entries: RegistryEntry[], viewer: NavViewer): VisibleModule[] {
  return visibleModules(entries, viewer).filter((module) => module.to !== null);
}

/** The manifests the viewer may enter, whatever surface they appear on. */
export function accessibleModules(
  entries: RegistryEntry[],
  viewer: NavViewer,
): ModuleManifest[] {
  return builtModules(entries).filter((module) => viewerMatches(module, viewer));
}

/**
 * Where signing in lands.
 *
 * A viewer with exactly one module goes straight into it (owner's call,
 * 2026-09-27, reversing the earlier "always land on the grid"): a pharmacy has
 * one door, and a launcher in front of one door is a click that asks a question
 * with one answer. Anyone with a choice still lands on the grid.
 *
 * The redirect is on the *landing*, never on the `/modules` route: making the
 * grid itself bounce would take "all apps" away from the switcher and would
 * flip behaviour mid-session the moment a second module is granted.
 */
export function landingFor(viewer: NavViewer, entries: RegistryEntry[] = MODULE_REGISTRY): string {
  const open = openableModules(entries, viewer);
  return open.length === 1 && open[0]?.to ? open[0].to : paths.modules;
}
